/**
 * batch-67 middelen-inname, fase B (FO-505 §B + §12.1-12.3) - het
 * reparatieverzoek: een beschadigd voertuig, uniek middel of overig middel wordt
 * met minimaal één foto aangemeld, door een goedkeurder beoordeeld en pas daarna
 * in reparatie genomen. Bij het verzoek hoort het verhaalbesluit (niet, eigen
 * risico, volledig of een deel) met 4-ogen boven een instelbaar bedrag.
 *
 * Gedeeld door de API (`.safeParse`), de backoffice (client-side validatie met
 * hetzelfde schema, inline veldfouten) en de portal (inzage en bezwaar).
 */
import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Vocabulaire (spiegel van de Prisma-enums) ────────────────────────────────

export const REPARATIE_VERZOEK_STATUSSEN = [
  'AANGEVRAAGD',
  'GOEDGEKEURD',
  'AFGEWEZEN',
  'IN_REPARATIE',
  'GEREPAREERD',
  'AFGESCHREVEN',
] as const
export type ReparatieVerzoekStatus = (typeof REPARATIE_VERZOEK_STATUSSEN)[number]
export const REPARATIE_VERZOEK_STATUS_LABELS: Record<ReparatieVerzoekStatus, string> = {
  AANGEVRAAGD: 'Aangevraagd',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEWEZEN: 'Afgewezen',
  IN_REPARATIE: 'In reparatie',
  GEREPAREERD: 'Gerepareerd',
  AFGESCHREVEN: 'Afgeschreven',
}

/** Een verzoek dat nog loopt: het object is niet vrij (uniek middel: niet uitgeefbaar). */
export const REPARATIE_LOPENDE_STATUSSEN = ['AANGEVRAAGD', 'GOEDGEKEURD', 'IN_REPARATIE'] as const
export function isReparatieLopend(status: ReparatieVerzoekStatus): boolean {
  return (REPARATIE_LOPENDE_STATUSSEN as readonly string[]).includes(status)
}

/**
 * De statusreeks uit FO-505 §12.1. Goedkeuren komt altijd vóór "in reparatie";
 * na goedkeuring kan er ook worden besloten het object af te schrijven
 * (reparatie duurder dan de dagwaarde, §5.3).
 */
export const REPARATIE_TRANSITIES: Record<ReparatieVerzoekStatus, readonly ReparatieVerzoekStatus[]> = {
  AANGEVRAAGD: ['GOEDGEKEURD', 'AFGEWEZEN'],
  GOEDGEKEURD: ['IN_REPARATIE', 'AFGESCHREVEN'],
  AFGEWEZEN: [],
  IN_REPARATIE: ['GEREPAREERD', 'AFGESCHREVEN'],
  GEREPAREERD: [],
  AFGESCHREVEN: [],
}

export function isReparatieOvergangToegestaan(van: ReparatieVerzoekStatus, naar: ReparatieVerzoekStatus): boolean {
  return REPARATIE_TRANSITIES[van].includes(naar)
}

export const REPARATIE_OBJECT_TYPES = ['VOERTUIG', 'UNIEK_ITEM', 'OVERIG'] as const
export type ReparatieObjectType = (typeof REPARATIE_OBJECT_TYPES)[number]
export const REPARATIE_OBJECT_TYPE_LABELS: Record<ReparatieObjectType, string> = {
  VOERTUIG: 'Voertuig',
  UNIEK_ITEM: 'Middel (telefoon, laptop, gereedschap)',
  OVERIG: 'Overig middel',
}

export const REPARATIE_OORZAKEN = ['SLIJTAGE', 'SCHULD', 'ONBEKEND'] as const
export type ReparatieOorzaak = (typeof REPARATIE_OORZAKEN)[number]
export const REPARATIE_OORZAAK_LABELS: Record<ReparatieOorzaak, string> = {
  SLIJTAGE: 'Slijtage',
  SCHULD: 'Schuld van de gebruiker',
  ONBEKEND: 'Onbekend',
}

// batch-70 uitrusting-controle (FO-531): CAMPAGNE = defect gemeld in de uitrustingscontrole.
export const REPARATIE_BRONNEN = ['HANDMATIG', 'INNAME', 'ITEM', 'VOERTUIG', 'SCHADEMELDING', 'GARAGE_SCHADE', 'CAMPAGNE'] as const
export type ReparatieBron = (typeof REPARATIE_BRONNEN)[number]
export const REPARATIE_BRON_LABELS: Record<ReparatieBron, string> = {
  HANDMATIG: 'Handmatig',
  INNAME: 'Inname',
  ITEM: 'Middel',
  VOERTUIG: 'Voertuig',
  SCHADEMELDING: 'Schademelding',
  GARAGE_SCHADE: 'Garagefactuur (schade)',
  CAMPAGNE: 'Uitrustingscontrole',
}

export const REPARATIE_VERHAAL_SOORTEN = ['NIET', 'EIGEN_RISICO', 'VOLLEDIG', 'DEEL'] as const
export type ReparatieVerhaalSoort = (typeof REPARATIE_VERHAAL_SOORTEN)[number]
export const REPARATIE_VERHAAL_SOORT_LABELS: Record<ReparatieVerhaalSoort, string> = {
  NIET: 'Niet verhalen',
  EIGEN_RISICO: 'Eigen risico verhalen',
  VOLLEDIG: 'Volledig verhalen',
  DEEL: 'Een deel verhalen',
}

export const REPARATIE_VERHAAL_STATUSSEN = ['OPEN', 'WACHT_OP_TWEEDE', 'DEFINITIEF'] as const
export type ReparatieVerhaalStatus = (typeof REPARATIE_VERHAAL_STATUSSEN)[number]
export const REPARATIE_VERHAAL_STATUS_LABELS: Record<ReparatieVerhaalStatus, string> = {
  OPEN: 'Nog geen besluit',
  WACHT_OP_TWEEDE: 'Wacht op tweede akkoord',
  DEFINITIEF: 'Besloten',
}

export const REPARATIE_VERREKEN_ROUTES = ['EINDAFREKENING', 'LOON'] as const
export type ReparatieVerrekenRoute = (typeof REPARATIE_VERREKEN_ROUTES)[number]
export const REPARATIE_VERREKEN_ROUTE_LABELS: Record<ReparatieVerrekenRoute, string> = {
  EINDAFREKENING: 'Via de eindafrekening (uit dienst)',
  LOON: 'Via het loon (taak salarisadministratie)',
}

// ─── DTO's ────────────────────────────────────────────────────────────────────

export interface ReparatieVerzoekAuditDTO {
  id: string
  actie: string
  actorNaam: string | null
  /** ISO-datetime. */
  op: string
  toelichting: string | null
}

/** Het verhaalbesluit zoals de backoffice het toont (met bedragen). */
export interface ReparatieVerhaalDTO {
  status: ReparatieVerhaalStatus
  soort: ReparatieVerhaalSoort | null
  /** Decimal-string EUR; null zolang er geen besluit is. */
  bedrag: string | null
  reden: string | null
  doorNaam: string | null
  /** ISO-datetime. */
  op: string | null
  tweedeDoorNaam: string | null
  tweedeOp: string | null
  /** Het besluit ligt boven de 4-ogen-drempel. */
  vierOgenVereist: boolean
  route: ReparatieVerrekenRoute | null
  verrekendOp: string | null
  verrekenNotitie: string | null
  medewerkerGemeldOp: string | null
  bezwaar: {
    op: string
    toelichting: string
    afgehandeldOp: string | null
    uitkomst: string | null
  } | null
}

export interface ReparatieVerzoekDTO {
  id: string
  objectType: ReparatieObjectType
  voertuigId: string | null
  uniekItemId: string | null
  objectOmschrijving: string
  objectKenmerk: string | null
  employeeId: string | null
  employeeNaam: string | null
  omschrijving: string
  oorzaak: ReparatieOorzaak
  /** Aantal foto's; de client bouwt zelf het same-origin proxy-pad (FO-372). */
  fotoAantal: number
  status: ReparatieVerzoekStatus
  bron: ReparatieBron
  schadeMeldingId: string | null
  garageKoppelingId: string | null
  inleverItemId: string | null
  voertuigAfspraakId: string | null
  /** Decimal-strings EUR. */
  kostenraming: string | null
  werkelijkeKosten: string | null
  dagwaarde: string | null
  dagwaardeToelichting: string | null
  /** §5.3: de (geraamde) reparatie is duurder dan de dagwaarde: afschrijven ligt voor de hand. */
  adviesAfschrijven: boolean
  ingediendDoorId: string
  ingediendDoorNaam: string | null
  /** ISO-datetime. */
  ingediendOp: string
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  beoordelingToelichting: string | null
  inReparatieOp: string | null
  afgerondOp: string | null
  afrondToelichting: string | null
  /**
   * De stand van het unieke middel zelf (alleen bij een verzoek voor een uniek
   * middel dat nog bestaat): bepaalt of afwijzen het middel kan vrijgeven.
   */
  middel: ReparatieMiddelStandDTO | null
  /**
   * Het verhaalbesluit; `null` als deze gebruiker het niet mag zien (alleen HR,
   * de salarisadministratie en de goedkeurders zien wat er op de medewerker
   * wordt verhaald en wat die daartegen inbrengt).
   */
  verhaal: ReparatieVerhaalDTO | null
  /** Wat de ingelogde gebruiker nu met dit verzoek mag (UI-hint; de server blijft de poort). */
  mag: {
    beoordelen: boolean
    /** De gebruiker is goedkeurder maar diende het verzoek zelf in (toon waarom de knop ontbreekt). */
    eigenVerzoek: boolean
    starten: boolean
    afronden: boolean
    /** Raming en werkelijke kosten bijwerken (zolang het verhaal niet is verrekend). */
    kostenBijwerken: boolean
    verhaalBesluiten: boolean
    verhaalTweedeAkkoord: boolean
    verrekendMelden: boolean
    bezwaarAfhandelen: boolean
  }
  audits: ReparatieVerzoekAuditDTO[]
}

/** Waar het unieke middel van een verzoek nu staat. */
export interface ReparatieMiddelStandDTO {
  /** `VoorraadUniekStatus` van het exemplaar. */
  status: string
  /** Het ligt nog bij een medewerker (dan verandert afwijzen niets aan het middel). */
  bijMedewerker: boolean
  /** Het lag op voorraad toen het verzoek werd ingediend (het verzoek zelf zette het op defect). */
  lagOpVoorraad: boolean
}

/** Rij in het overzicht (zonder audits). */
export type ReparatieVerzoekLijstRijDTO = Omit<ReparatieVerzoekDTO, 'audits' | 'mag' | 'middel'>

/** Een voorstel uit een schademelding of garage-schadetoewijzing: wordt pas een verzoek als iemand het overneemt. */
export interface ReparatieVoorstelDTO {
  bron: Extract<ReparatieBron, 'SCHADEMELDING' | 'GARAGE_SCHADE'>
  /** `SchadeMelding.id` of de id van de garagekoppeling. */
  bronId: string
  voertuigId: string
  objectOmschrijving: string
  objectKenmerk: string | null
  employeeId: string | null
  employeeNaam: string | null
  /** ISO-dag (yyyy-mm-dd) van de schade. */
  datum: string | null
  omschrijving: string
  /** Decimal-strings EUR. */
  bedrag: string | null
  eigenRisico: string | null
}

/** Wat de ingelogde gebruiker met reparatieverzoeken mag (UI-hint; de routes blijven de poort). */
export interface ReparatieRechtenDTO {
  inzien: boolean
  /** Mag voor ten minste één soort object een verzoek indienen. */
  indienen: boolean
  /** De soorten object waarvoor hij een verzoek mag indienen (en de reparatie beheert). */
  indienbareTypes: ReparatieObjectType[]
  /** In de lijst van goedkeurders (of de terugval als die leeg is). */
  goedkeurder: boolean
  verhaalBesluiten: boolean
  /** Mag het verhaal en het bezwaar zien (HR, salarisadministratie, goedkeurders). */
  verhaalZien: boolean
  /** Mag de voorstellen uit schademeldingen en garagefacturen zien (wagenpark). */
  voorstellen: boolean
}

/** Portal: een verhaalbesluit dat de medewerker betreft. Geen kostprijs, geen garagefactuur. */
export interface MijnVerhaalDTO {
  id: string
  objectOmschrijving: string
  omschrijving: string
  soort: ReparatieVerhaalSoort
  /** Decimal-string EUR: het bedrag dat op de medewerker wordt verhaald. */
  bedrag: string
  reden: string | null
  /** ISO-datetime van het besluit. */
  besluitOp: string
  route: ReparatieVerrekenRoute | null
  fotoAantal: number
  /** ISO-dag tot wanneer bezwaar mogelijk is; null = termijn verstreken of al bezwaar gemaakt. */
  bezwaarTot: string | null
  bezwaar: { op: string; toelichting: string; afgehandeldOp: string | null; uitkomst: string | null } | null
}

// ─── Schema's ─────────────────────────────────────────────────────────────────

/** Bedrag in euro's als tekst ("12.50" of "12,50"), hooguit twee decimalen. */
export const bedragTekst = z
  .string()
  .trim()
  .regex(/^\d{1,8}([.,]\d{1,2})?$/, 'Vul een bedrag in, bijvoorbeeld 125,00.')
  .transform((v) => v.replace(',', '.'))

const fotoId = z.string().uuid('Ongeldige foto-verwijzing')

export const ReparatieVerzoekAanmakenSchema = z
  .object({
    objectType: z.enum(REPARATIE_OBJECT_TYPES),
    voertuigId: idParam('voertuigId').nullable().optional(),
    uniekItemId: idParam('uniekItemId').nullable().optional(),
    /** Verplicht bij OVERIG: wat is er kapot. */
    objectOmschrijving: z.string().trim().max(200).nullable().optional(),
    /** De verantwoordelijke medewerker; leeg = niet toewijsbaar. */
    employeeId: idParam('employeeId').nullable().optional(),
    omschrijving: z.string().trim().min(5, 'Beschrijf wat er kapot is (minimaal 5 tekens).').max(4000),
    oorzaak: z.enum(REPARATIE_OORZAKEN).default('ONBEKEND'),
    /** Vooraf geuploade foto's (client-UUID via `POST /middelen/fotos`). Minimaal één (§12.1). */
    fotoIds: z.array(fotoId).max(10, "Maximaal 10 foto's."),
    kostenraming: bedragTekst.nullable().optional(),
    bron: z.enum(REPARATIE_BRONNEN).default('HANDMATIG'),
    schadeMeldingId: idParam('schadeMeldingId').nullable().optional(),
    garageKoppelingId: idParam('garageKoppelingId').nullable().optional(),
    inleverItemId: idParam('inleverItemId').nullable().optional(),
  })
  .superRefine((val, ctx) => {
    // Vanuit een inlever-regel tellen de foto's van de inname mee; de server
    // controleert dan dat er in totaal minstens één is.
    if (val.fotoIds.length < 1 && !val.inleverItemId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fotoIds'], message: 'Voeg minimaal één foto van de schade toe.' })
    }
    // Vanuit een inlever-regel leidt de server het object uit die regel af.
    if (val.objectType === 'VOERTUIG' && !val.voertuigId && !val.inleverItemId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['voertuigId'], message: 'Kies het voertuig.' })
    }
    if (val.objectType === 'UNIEK_ITEM' && !val.uniekItemId && !val.inleverItemId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['uniekItemId'], message: 'Kies het middel.' })
    }
    if (val.objectType === 'OVERIG' && !val.inleverItemId && (!val.objectOmschrijving || val.objectOmschrijving.length < 2)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['objectOmschrijving'], message: 'Geef aan om welk middel het gaat.' })
    }
  })
export type ReparatieVerzoekAanmakenInput = z.infer<typeof ReparatieVerzoekAanmakenSchema>
export type ReparatieVerzoekAanmakenPayload = z.input<typeof ReparatieVerzoekAanmakenSchema>

export const ReparatieBeoordelenSchema = z
  .object({
    besluit: z.enum(['GOEDKEUREN', 'AFWIJZEN']),
    toelichting: z.string().trim().max(2000).nullable().optional(),
    /**
     * Alleen bij afwijzen van een uniek middel dat als defect in het magazijn
     * ligt: is het middel in orde (dan gaat het terug op voorraad en is het weer
     * uit te geven) of blijft het defect? Leeg = wat het was voor het verzoek:
     * lag het op voorraad, dan gaat het daar weer naartoe.
     */
    middelInOrde: z.boolean().nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.besluit === 'AFWIJZEN' && (!val.toelichting || val.toelichting.length < 3)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toelichting'], message: 'Geef aan waarom je het verzoek afwijst.' })
    }
  })
export type ReparatieBeoordelenInput = z.infer<typeof ReparatieBeoordelenSchema>

const isoDag = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet yyyy-mm-dd zijn')

/** In reparatie nemen. Voertuig: optioneel meteen de afspraak (soort REPARATIE) plannen. */
export const ReparatieStartSchema = z.object({
  /** Uniek middel dat nog bij de medewerker ligt: de locatie waar het wordt ingenomen. */
  locatieId: idParam('locatieId').nullable().optional(),
  /** Voertuig: datum en garage van de afspraak; leeg = afspraak "te plannen". */
  afspraakDatum: isoDag.nullable().optional(),
  afspraakTijd: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Tijd moet uu:mm zijn')
    .nullable()
    .optional(),
  garageClientId: idParam('garageClientId').nullable().optional(),
  garageNaam: z.string().trim().max(200).nullable().optional(),
  /** Voertuig: geen afspraak aanmaken (de reparatie loopt buiten de garageplanning). */
  zonderAfspraak: z.boolean().optional(),
  toelichting: z.string().trim().max(2000).nullable().optional(),
})
export type ReparatieStartInput = z.infer<typeof ReparatieStartSchema>

export const ReparatieAfrondenSchema = z
  .object({
    uitkomst: z.enum(['GEREPAREERD', 'AFGESCHREVEN']),
    werkelijkeKosten: bedragTekst.nullable().optional(),
    /** Uniek middel dat gerepareerd is: de voorraadlocatie waar het weer komt te liggen. */
    locatieId: idParam('locatieId').nullable().optional(),
    toelichting: z.string().trim().max(2000).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.uitkomst === 'AFGESCHREVEN' && (!val.toelichting || val.toelichting.length < 3)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['toelichting'], message: 'Geef aan waarom het wordt afgeschreven.' })
    }
  })
export type ReparatieAfrondenInput = z.infer<typeof ReparatieAfrondenSchema>

/** Kosten bijwerken (raming of werkelijke kosten) zolang het verzoek loopt of net is afgerond. */
export const ReparatieKostenSchema = z.object({
  kostenraming: bedragTekst.nullable().optional(),
  werkelijkeKosten: bedragTekst.nullable().optional(),
})
export type ReparatieKostenInput = z.infer<typeof ReparatieKostenSchema>

export const ReparatieVerhaalSchema = z
  .object({
    soort: z.enum(REPARATIE_VERHAAL_SOORTEN),
    /** Verplicht bij EIGEN_RISICO en DEEL; bij VOLLEDIG rekent de server met de (geraamde) kosten. */
    bedrag: bedragTekst.nullable().optional(),
    reden: z.string().trim().min(3, 'Geef de reden van het besluit (minimaal 3 tekens).').max(2000),
  })
  .superRefine((val, ctx) => {
    if ((val.soort === 'EIGEN_RISICO' || val.soort === 'DEEL') && (!val.bedrag || Number(val.bedrag) <= 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['bedrag'], message: 'Vul het bedrag in dat wordt verhaald (groter dan 0).' })
    }
  })
export type ReparatieVerhaalInput = z.infer<typeof ReparatieVerhaalSchema>
export type ReparatieVerhaalPayload = z.input<typeof ReparatieVerhaalSchema>

export const ReparatieVerhaalAkkoordSchema = z.object({
  toelichting: z.string().trim().max(2000).nullable().optional(),
})
export type ReparatieVerhaalAkkoordInput = z.infer<typeof ReparatieVerhaalAkkoordSchema>

/** De salarisadministratie meldt dat het verhaalde bedrag via het loon is verrekend. */
export const ReparatieVerrekendSchema = z.object({
  notitie: z.string().trim().min(3, 'Vermeld de periode of de wijze van verrekenen.').max(2000),
})
export type ReparatieVerrekendInput = z.infer<typeof ReparatieVerrekendSchema>

/** Bezwaar van de medewerker (portal). */
export const VerhaalBezwaarSchema = z.object({
  toelichting: z.string().trim().min(10, 'Licht je bezwaar toe (minimaal 10 tekens).').max(4000),
})
export type VerhaalBezwaarInput = z.infer<typeof VerhaalBezwaarSchema>

/** HR handelt het bezwaar af; het besluit zelf herzien kan daarna via een nieuw verhaalbesluit. */
export const BezwaarAfhandelenSchema = z.object({
  uitkomst: z.string().trim().min(3, 'Beschrijf de uitkomst van het bezwaar (minimaal 3 tekens).').max(4000),
})
export type BezwaarAfhandelenInput = z.infer<typeof BezwaarAfhandelenSchema>

export const ReparatieLijstQuerySchema = z.object({
  status: z.enum(REPARATIE_VERZOEK_STATUSSEN).optional(),
  /** `LOPEND` = aangevraagd, goedgekeurd of in reparatie. */
  groep: z.enum(['LOPEND', 'AFGEROND', 'TE_BEOORDELEN', 'VERHAAL_OPEN']).optional(),
  objectType: z.enum(REPARATIE_OBJECT_TYPES).optional(),
  employeeId: idParam('employeeId').optional(),
  voertuigId: idParam('voertuigId').optional(),
  uniekItemId: idParam('uniekItemId').optional(),
  search: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type ReparatieLijstQuery = z.infer<typeof ReparatieLijstQuerySchema>
